import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import * as store from "./lib/store";

const Ctx = createContext(null);
export const useField = () => useContext(Ctx);

export function FieldProvider({ children }) {
  const [online, setOnline] = useState(navigator.onLine);
  const [syncing, setSyncing] = useState(false);
  const [pending, setPending] = useState(0);
  const [org, setOrg] = useState(null);
  const [meta, setMeta] = useState(null);
  const [lastSync, setLastSync] = useState(null);
  const [version, setVersion] = useState(0);
  const busy = useRef(false);

  const bump = useCallback(() => setVersion((v) => v + 1), []);
  const refreshPending = useCallback(async () => setPending(await store.pendingCount()), []);

  const sync = useCallback(async () => {
    if (busy.current) return;
    busy.current = true;
    if (navigator.onLine) {
      setSyncing(true);
      try {
        await store.push();
        const data = await store.pull();
        if (data?.org) setOrg(data.org);
        const m = await store.refreshMeta().catch(() => null);
        if (m) setMeta(m);
        setLastSync(await store.getLastSync());
        bump();
      } catch (e) {
        /* offline / transient */
      } finally {
        setSyncing(false);
      }
    }
    await refreshPending();
    busy.current = false;
  }, [bump, refreshPending]);

  useEffect(() => {
    const goOnline = () => {
      setOnline(true);
      sync();
    };
    const goOffline = () => setOnline(false);
    window.addEventListener("online", goOnline);
    window.addEventListener("offline", goOffline);
    (async () => {
      setOrg(await store.getOrg());
      setMeta(await store.getMeta());
      setLastSync(await store.getLastSync());
      await refreshPending();
      bump();
      sync();
    })();
    const t = setInterval(() => navigator.onLine && sync(), 45000);
    return () => {
      window.removeEventListener("online", goOnline);
      window.removeEventListener("offline", goOffline);
      clearInterval(t);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const value = { online, syncing, pending, org, meta, lastSync, version, sync, bump, refreshPending, store };
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}
